/**
 * ConfirmReasonModal — replaces `window.prompt`/`window.confirm` so
 * freeze/cancel flows are styled, keyboard-friendly, and (most importantly)
 * E2E-testable via data-testid selectors.
 *
 * Generic: the parent supplies title, body copy, label, and submit verb.
 */

import React, { useEffect, useState } from 'react';
import { Button, Input, Modal } from '@/components/common';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void | Promise<void>;
  title: string;
  description?: string;
  reasonLabel?: string;
  confirmLabel?: string;
  /** Pass a unique slug per usage so test selectors are stable + distinct. */
  testIdPrefix: string;
  /** When true, button is disabled (e.g. in-flight). */
  busy?: boolean;
  /** Optional auxiliary controls (e.g. a date picker for freeze-until). */
  extraFields?: React.ReactNode;
}

export const ConfirmReasonModal: React.FC<Props> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  description,
  reasonLabel = 'Reason',
  confirmLabel = 'Confirm',
  testIdPrefix,
  busy = false,
  extraFields,
}) => {
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (!isOpen) setReason('');
  }, [isOpen]);

  const submit = async () => {
    if (!reason.trim()) return;
    await onConfirm(reason.trim());
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      size="sm"
      data-testid={`${testIdPrefix}-modal`}
    >
      {description && (
        <p
          data-testid={`${testIdPrefix}-description`}
          style={{ marginTop: 0, color: '#475569', fontSize: 14 }}
        >
          {description}
        </p>
      )}
      {extraFields}
      <label style={{ display: 'block', fontSize: 13, marginBottom: 4 }}>
        {reasonLabel}
      </label>
      <Input
        autoFocus
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        data-testid={`${testIdPrefix}-reason-input`}
        placeholder="Brief explanation…"
      />
      <div
        style={{
          display: 'flex',
          gap: 8,
          justifyContent: 'flex-end',
          marginTop: 16,
        }}
      >
        <Button
          variant="ghost"
          onClick={onClose}
          data-testid={`${testIdPrefix}-cancel-btn`}
        >
          Back
        </Button>
        <Button
          onClick={submit}
          disabled={busy || !reason.trim()}
          data-testid={`${testIdPrefix}-submit-btn`}
        >
          {busy ? 'Saving…' : confirmLabel}
        </Button>
      </div>
    </Modal>
  );
};

